import React, { ReactNode, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { platformActions, RootStatePlatform } from "../store/platform-slice";
import { getHomePage } from "../../../common/network/Api";
import CustomDatePicker from "../components/dashboard/CustomDatePicker";
import { homePageTabs } from "../store/helper";
import SecondaryTabMenuPicker from "../components/common/SecondaryTabMenuPicker";
import OverviewTab from "../components/dashboard/dashboardTabs/OverviewTab";
import ActivityTab from "../components/dashboard/dashboardTabs/ActivityTab";
import NutritionTab from "../components/dashboard/dashboardTabs/NutritionTab";
import RecoveryTab from "../components/dashboard/dashboardTabs/RecoveryTab";
import ServiceSection from "../components/dashboard/ServiceSection";
import CoreMetricCard, {
  CoreMetricCardType,
} from "../components/dashboard/CoreMetricCard";
import AgeClock from "../components/dashboard/BiologicalAgeGraph/AgeClock";
import WeightInfo from "../components/dashboard/WeightInfo";
import { HomeObjectType } from "../store/HomeObjectType";
import HomeSectionSkeleton from "../components/dashboard/dashboardTabs/HomeSectionSkeleton";
import {
  MetricsWidgetSkeleton,
  ReportCardSkeleton,
  ServicesSectionSkeleton,
  UserProgressSkeleton,
} from "../components/dashboard/DashboardReportSkeleton";
import { useSubject } from "../providers/SubjectProvider";
import {
  isResourcePending,
  usePlatformResource,
} from "../hooks/usePlatformResource";
import NextBreathAnalysis from "../components/dashboard/NextBreathAnalysis";
import OverviewMetricsWidget from "../components/dashboard/OverviewMetricsWidget";
import { useAuth } from "../../../providers/AuthProvider";
import useTheme from "../../../common/hooks/useTheme";
import UserProgress from "../components/dashboard/UserProgress";
import InfoIcon from "../svg/InfoIcon";
import { Theme } from "../../../providers/ThemeProvider";
import TreatmentPlanCard from "../components/programming/TreatmentPlanCard";
import {
  ServicesPage,
  ServiceContent,
} from "../../new-reports/types/newReportTypes";
import ServiceViewModal from "../components/programming/ServiceViewModal";
import ScrollableWrapper from "../components/common/ScrollableWrapper";
import StartConsultationCard from "../../consultation/components/shared/StartConsultationCard";

/**
 * Dims a section when the report is demo data.
 *
 * Accepts `style` because this wrapper sits *between* the dashboard's flex row
 * and the column inside it. Without the column's flex/min/max applied here, the
 * wrapper defaults to `flex: 0 1 auto` and sizes to its content — so the columns
 * were one width while showing skeletons and another once the real content
 * arrived. The sizing has to live on the flex item itself.
 */
export const DemoReportOverlay = ({
  children,
  style,
}: {
  children: ReactNode;
  style?: React.CSSProperties;
}) => {
  const isDemoReport = useSelector(
    (state: RootStatePlatform) => state.platform.dataPlatformServer.isDemo
  );
  return (
    <div
      style={{
        opacity: isDemoReport ? 0.5 : 1,
        ...style,
      }}
    >
      {children}
    </div>
  );
};

export const DemoReportMessage = () => {
  const theme: Theme = useTheme();

  return (
    <div style={styles.demoReportMessageContainer(theme)}>
      <InfoIcon size={12} />
      <div style={styles.demoReportMessage(theme)}>
        Faded sections display sample data and are not editable.
      </div>
    </div>
  );
};

const DashboardScreen = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoadingDataNut, setIsLoadingDataNut] = useState(true);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [selectedService, setSelectedService] = useState<ServiceContent | null>(
    null
  );
  const [serviceModalOpen, setServiceModalOpen] = useState(false);
  const { AuthUser, AuthIsSimpleUser } = useAuth();
  const dispatch = useDispatch();

  const ASActiveDate = useSelector(
    (state: RootStatePlatform) => state.platform.ASActiveDate
  );

  const reportData = useSelector(
    (state: RootStatePlatform) => state.platform.dataPlatformServer
  );

  // The report (GetReportv2, ~8s) is no longer front-loaded — this page asks for
  // it and renders the blocks that need it behind skeletons meanwhile. The
  // nutrition column above is driven by its own faster call and stays live.
  const { subjectId } = useSubject();
  const reportStatus = usePlatformResource("report", subjectId, AuthUser.role);
  usePlatformResource("integrations", subjectId, AuthUser.role);
  const isReportPending = isResourcePending(reportStatus);

  const servicesData = reportData.prescriptionPlan?.find(
    (p) => p.id === "services-page-1" && p.pageType === "services"
  ) as ServicesPage | undefined;

  const handleServiceCardClick = (service: ServiceContent) => {
    setSelectedService(service);
    setServiceModalOpen(true);
  };

  const handleServiceModalClose = () => {
    setServiceModalOpen(false);
    setSelectedService(null);
  };

  const handleViewAllServices = () => {
    const basePath = AuthIsSimpleUser ? "/platform" : "/client";
    const searchParams = location.search; // Preserve current query params (e.g., ?id=...)
    navigate(`${basePath}/programming/bio-optimization${searchParams}`);
  };

  const getDataFromNut = async () => {
    try {
      setIsLoadingDataNut(true);
      let dataNut: HomeObjectType = await getHomePage(
        ASActiveDate,
        AuthUser.role
      );
      dispatch(platformActions.updateDataNutServer(dataNut));
    } catch (e: any) {
      console.log(e);
    } finally {
      setIsLoadingDataNut(false);
    }
  };

  useEffect(() => {
    getDataFromNut();
  }, [ASActiveDate]);

  return (
    <div>
      <div style={styles.headerWrapper}>
        {/* Placeholder rather than nothing: UserProgress renders null without a
            report, which would collapse the header. */}
        {isReportPending ? <UserProgressSkeleton /> : <UserProgress />}
        {/* The "Charts" button that opened this chart in a modal is gone — the
            chart is a page of its own now, at History › Timeline. */}
      </div>
      {reportData.isDemo && (
        <div style={styles.demoReportWrapper}>
          <DemoReportMessage />
        </div>
      )}
      <div style={styles.container}>
        <div
          style={{
            ...styles.section,
            border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
            borderRadius: "8px",
            padding: "8px",
            minWidth: "415px",
            backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
          }}
        >
          <CustomDatePicker isLoadingDataNut={isLoadingDataNut} />
          <div style={{ height: "24px" }} />
          <div style={{ width: "100%" }}>
            <SecondaryTabMenuPicker
              tabs={homePageTabs}
              selectedTab={selectedTab}
              setSelectedTab={setSelectedTab}
            />
          </div>

          {isLoadingDataNut ? (
            <HomeSectionSkeleton selectedTab={selectedTab} />
          ) : (
            <div style={{ width: "100%" }}>
              {selectedTab === 0 && <OverviewTab />}
              {selectedTab === 1 && <ActivityTab />}
              {selectedTab === 2 && <NutritionTab />}
              {selectedTab === 3 && <RecoveryTab />}
            </div>
          )}
        </div>
        <DemoReportOverlay
          style={{ flex: 1, minWidth: "410px", maxWidth: "435px" }}
        >
          <div
            style={{
              ...styles.section,
              width: "100%",
            }}
          >
            <AgeClock isLoadingDataNut={isLoadingDataNut} />
            <div style={styles.coreMetricCardsContainer}>
              <CoreMetricCard
                isLoadingDataNut={isLoadingDataNut}
                title={"Metabolic Rate"}
                type={CoreMetricCardType.metabolicRate}
              />
              <CoreMetricCard
                isLoadingDataNut={isLoadingDataNut}
                title={"Fat Burn"}
                type={CoreMetricCardType.fatBurn}
              />
            </div>
            {/*<WeightInfo />*/}
            {isReportPending ? (
              <>
                <MetricsWidgetSkeleton />
                <MetricsWidgetSkeleton />
              </>
            ) : (
              <>
                <OverviewMetricsWidget
                  data={reportData.dashboard?.metrics}
                  title={"PNOE scores"}
                  footerMessage={
                    "To view the full report with detailed PNOE test, click"
                  }
                  type="metric"
                />
                <OverviewMetricsWidget
                  data={reportData.dashboard?.bloodbiomarkers}
                  title={"Areas of focus"}
                  footerMessage={
                    "To view the full report with detailed biomarkers, click"
                  }
                  type="biomarker"
                />
              </>
            )}
          </div>
        </DemoReportOverlay>
        <DemoReportOverlay
          style={{ flex: 1, minWidth: "410px", maxWidth: "470px" }}
        >
          <div
            style={{
              ...styles.section,
              width: "100%",
            }}
          >
            {isReportPending ? (
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                <ReportCardSkeleton height={140} />
                <ReportCardSkeleton height={120} />
                <ServicesSectionSkeleton />
              </div>
            ) : (
              <>
                <StartConsultationCard />
                <NextBreathAnalysis />
              </>
            )}

            {/* Available Services Section */}
            {!isReportPending &&
              servicesData &&
              servicesData.services &&
              servicesData.services.length > 0 && (
                <div style={styles.servicesSection}>
                  <div style={styles.servicesTitleRow}>
                    <div style={styles.servicesTitle(theme)}>Services</div>
                    <div
                      style={styles.viewAllButton(theme)}
                      onClick={handleViewAllServices}
                    >
                      View all
                    </div>
                  </div>
                  <div style={styles.servicesScrollContainer}>
                    <ScrollableWrapper customPadding="8px">
                      <div style={styles.servicesGrid}>
                        {servicesData.services.map((service, index: number) => (
                          <div key={index} style={styles.serviceCardWrapper}>
                            <TreatmentPlanCard
                              service={service}
                              onClick={() => handleServiceCardClick(service)}
                              showAttributes={false}
                              compact={true}
                            />
                          </div>
                        ))}
                      </div>
                    </ScrollableWrapper>
                  </div>
                </div>
              )}
          </div>
        </DemoReportOverlay>
      </div>
      {servicesData?.labels && (
        <ServiceViewModal
          open={serviceModalOpen}
          onClose={handleServiceModalClose}
          service={selectedService}
          labels={servicesData.labels}
          unitType={servicesData.unitType}
        />
      )}
    </div>
  );
};

export default DashboardScreen;

const styles = {
  headerWrapper: { paddingBottom: "24px", display: "flex" },
  container: {
    width: "100%",
    display: "flex",
    gap: "16px",
    flexDirection: "row" as "row",
    position: "relative" as "relative",
  },
  section: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column" as "column",
    alignItems: "center",
  },
  coreMetricCardsContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "row" as "row",
    gap: "8px",
    marginTop: "32px",
  },
  demoReportWrapper: {
    paddingBottom: "24px",
  },
  demoReportMessageContainer: (theme: Theme) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: "8px",
    padding: "8px 12px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    width: "fit-content",
  }),
  demoReportMessage: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    lineHeight: "16px",
  }),
  servicesSection: {
    marginTop: "24px",
    width: "100%",
  },
  servicesTitleRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
  },
  servicesTitle: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "16px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  viewAllButton: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colors_ds.PRIMARY_TEXT_MAIN,
    cursor: "pointer",
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  }),
  servicesScrollContainer: {
    display: "flex",
    flexDirection: "column" as "column",
    height: "670px",
    minHeight: 0,
  },
  servicesGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "16px",
    width: "100%",
  },
  serviceCardWrapper: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
  },
};
